import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ProgrammeContext,
  UserContext,
} from "../../contexts/UserContext";

import {
  Image,
  LogoutButton,
} from "../../modules/components";

import useAxiosInterceptors from "../../hooks/httpConfig";
import { getProfileUrl } from "../../utils/utilisateur.utils";

const formatTime = (dateString) => {
  if (!dateString) {
    return "";
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const differenceInMinutes = Math.max(
    0,
    Math.floor(
      (Date.now() - date.getTime()) / 60000,
    ),
  );

  if (differenceInMinutes < 1) {
    return "À l'instant";
  }

  if (differenceInMinutes < 60) {
    return `Il y a ${differenceInMinutes} min`;
  }

  const differenceInHours = Math.floor(
    differenceInMinutes / 60,
  );

  if (differenceInHours < 24) {
    return `Il y a ${differenceInHours} h`;
  }

  const differenceInDays = Math.floor(
    differenceInHours / 24,
  );

  return `Il y a ${differenceInDays} j`;
};

const HeaderTop = () => {
  const apiInstance = useAxiosInterceptors();

  const [user] = useContext(UserContext);
  const [projet] = useContext(ProgrammeContext);

  const [notifications, setNotifications] =
    useState([]);

  const [notificationsLoading, setNotificationsLoading] =
    useState(false);

  const userEmail = user?.email;
  const programmeNom = projet?.programme?.nom;

  const profileUrl = useMemo(
    () => getProfileUrl(user),
    [user],
  );

  const userFullName = useMemo(
    () =>
      [
        user?.nom,
        user?.prenom,
      ]
        .filter(Boolean)
        .join(" "),
    [
      user?.nom,
      user?.prenom,
    ],
  );

  const programmeDescription = useMemo(() => {
    const role = projet?.role?.libelle;

    return [
      programmeNom,
      role,
    ]
      .filter(Boolean)
      .join(" - ");
  }, [
    programmeNom,
    projet?.role?.libelle,
  ]);

  const fetchNotifications =
    useCallback(async () => {
      if (!userEmail || !programmeNom) {
        setNotifications([]);
        return;
      }

      setNotificationsLoading(true);

      try {
        const encodedEmail =
          encodeURIComponent(userEmail);

        const encodedProgramme =
          encodeURIComponent(programmeNom);

        const { data } =
          await apiInstance.get(
            `/notifications/recipient/${encodedEmail}/${encodedProgramme}`,
          );

        const notificationList =
          data?.notifications ?? data;

        setNotifications(
          Array.isArray(notificationList)
            ? notificationList
            : [],
        );
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des notifications :",
          error,
        );

        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    }, [
      apiInstance,
      programmeNom,
      userEmail,
    ]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <div className="HeaderTop">
      <div className="HeaderLeft-Side" />

      <div className="HeaderRight-Side">
        <div className="UserBox">
          <div id="User-DropDown">
            <div className="UserBox-Infos">
              <label>
                {userFullName}
              </label>

              <span>
                {programmeDescription}
              </span>
            </div>

            <div className="UserBox-Thumb">
              {profileUrl && (
                <Image
                  fileName={profileUrl}
                  className="thumbnail-profile"
                />
              )}
            </div>
          </div>

          <div className="User-DropDownP">
            <ul>
              <li>
                <a href="/profile">
                  <i className="uil uil-user-md" />

                  <span>
                    Mon profil
                  </span>
                </a>
              </li>

              <li>
                <LogoutButton
                  hasIcon={true}
                />
              </li>
            </ul>
          </div>
        </div>

        <div className="AlertBox">
          <button
            className="AlertBox-BTN"
            type="button"
            title="Notifications"
            onClick={fetchNotifications}
            disabled={notificationsLoading}
          >
            <i className="uil uil-bell" />
          </button>

          <div className="NotificationsContainer">
            <div className="NotificationsContent-BOX">
              <h4>
                Notifications
              </h4>

              <div className="NotificationsItems">
                {notificationsLoading && (
                  <p
                    className="NotificationItem-Text"
                    style={{
                      padding: "10px",
                    }}
                  >
                    Chargement...
                  </p>
                )}

                {!notificationsLoading &&
                  notifications.length === 0 && (
                    <p
                      className="NotificationItem-Text"
                      style={{
                        padding: "10px",
                      }}
                    >
                      Aucune notification
                    </p>
                  )}

                {!notificationsLoading &&
                  notifications.map(
                    (notification) => (
                      <div
                        className="NotificationItem"
                        key={
                          notification?._id ||
                          `${notification?.createdAt}-${notification?.message}`
                        }
                      >
                        <div className="NotificationItem-Thumb" />

                        <div className="NotificationItem-Content">
                          <span className="NotificationItem-Text">
                            {notification?.message ||
                              notification?.titre ||
                              "Nouvelle notification"}
                          </span>

                          <span className="NotificationItem-Time">
                            {formatTime(
                              notification?.createdAt,
                            )}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
              </div>

              <a
                className="Notifications-ShowMore"
                href="/flux"
              >
                Afficher tout
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderTop;
